import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Check, Clock, Download, Eye, FileText, X } from 'lucide-react';
import { toast } from 'sonner';

import { adminApi } from '../api/examApi';
import { DashboardLayout } from '../components/layout';
import HomeworkFileViewerModal from '../components/HomeworkFileViewerModal';
import { parseApiError } from '../lib/apiErrors';

const HOMEWORK_BADGE = {
  done: { label: 'Done', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300', icon: Check },
  not_done: { label: 'Not Done', color: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300', icon: X },
  pending: { label: 'Pending', color: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300', icon: Clock },
};

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function AdminClassroomDetailPage() {
  const { entryId } = useParams();
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null); // { submissionId, title }

  const loadEntry = useCallback(() => {
    setLoading(true);
    adminApi.getClassEntry(entryId)
      .then(({ data }) => setEntry(data))
      .catch((err) => toast.error(parseApiError(err, 'Failed to load class entry').message))
      .finally(() => setLoading(false));
  }, [entryId]);

  useEffect(() => { loadEntry(); }, [loadEntry]);

  const handleDownload = async (file) => {
    try {
      const res = await adminApi.downloadClassEntryFile(entryId, file.id);
      downloadBlob(res.data, `${entry.title}_${file.kind}${file.id.slice(0, 8)}`);
    } catch (err) {
      toast.error(parseApiError(err, 'Download failed').message);
    }
  };

  if (loading || !entry) {
    return (
      <DashboardLayout title="Classroom Entry">
        <div className="animate-pulse space-y-4 max-w-3xl">
          <div className="h-8 w-64 rounded bg-slate-200 dark:bg-slate-700" />
          <div className="h-32 rounded bg-slate-100 dark:bg-slate-800" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Classroom Entry">
      <div className="animate-fade-in max-w-3xl space-y-6">
        <Link
          to="/admin/classroom"
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
        >
          <ArrowLeft size={16} /> Back to Classroom Oversight
        </Link>

        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 shadow-sm">
          <h2 className="text-2xl font-heading font-bold text-slate-900 dark:text-white">{entry.title}</h2>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            {entry.group_name} &middot; {new Date(`${entry.date}T00:00:00`).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })} &middot; by {entry.faculty_name}
          </p>
          {entry.notes_message && (
            <div className="mt-4">
              <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1">Notes / Message</p>
              <p className="text-sm text-slate-700 dark:text-slate-300">{entry.notes_message}</p>
            </div>
          )}
          {entry.faculty_comment && (
            <div className="mt-3">
              <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1">Faculty Comment</p>
              <p className="text-sm text-slate-700 dark:text-slate-300">{entry.faculty_comment}</p>
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 shadow-sm">
          <h3 className="mb-3 font-medium text-slate-900 dark:text-white">Files</h3>
          {entry.files.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">No files uploaded yet.</p>
          ) : (
            <div className="divide-y divide-slate-100 dark:divide-slate-700">
              {entry.files.map((file) => (
                <div key={file.id} className="flex items-center justify-between py-2.5" data-testid={`entry-file-${file.id}`}>
                  <div className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
                    <FileText size={16} className="text-slate-400" />
                    <span className="capitalize font-medium">{file.kind}</span>
                    <span className="text-xs text-slate-400">
                      {new Date(file.uploaded_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
                    </span>
                  </div>
                  <button
                    onClick={() => handleDownload(file)}
                    className="inline-flex items-center gap-1 text-primary dark:text-accent-cool hover:underline text-sm"
                  >
                    <Download size={14} /> Download
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden shadow-sm">
          <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-700">
            <h3 className="font-medium text-slate-900 dark:text-white">
              Homework Roster ({entry.homework_roster.length})
            </h3>
          </div>
          {entry.homework_roster.length === 0 ? (
            <p className="px-6 py-6 text-sm text-slate-400">No approved students in this group.</p>
          ) : (
            <div className="divide-y divide-slate-100 dark:divide-slate-700">
              {entry.homework_roster.map((r) => {
                const badge = HOMEWORK_BADGE[r.status] || HOMEWORK_BADGE.pending;
                const BadgeIcon = badge.icon;
                return (
                  <div key={r.student_id} className="space-y-2 px-6 py-3" data-testid={`homework-row-${r.student_id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium text-slate-900 dark:text-white">{r.student_name}</span>
                      <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${badge.color}`}>
                        <BadgeIcon size={12} /> {badge.label}
                      </span>
                    </div>
                    {r.files?.length ? (
                      <div className="flex flex-wrap gap-2" data-testid={`student-files-${r.student_id}`}>
                        {r.files.map((f) => (
                          <button
                            key={f.id}
                            onClick={() => setViewing({ submissionId: f.id, title: `${r.student_name} — ${f.original_name}` })}
                            className="inline-flex max-w-full items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
                            data-testid={`student-file-${f.id}`}
                          >
                            <Eye size={12} className="shrink-0" />
                            <span className="truncate">{f.original_name}</span>
                            <span className="shrink-0 text-slate-400">{new Date(f.uploaded_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}</span>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-400" data-testid={`student-files-${r.student_id}`}>No upload</p>
                    )}
                    {r.feedback && (
                      <div className="rounded-md bg-slate-50 p-3 dark:bg-slate-900/40" data-testid={`feedback-${r.student_id}`}>
                        <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">Feedback</p>
                        <p className="mt-1 whitespace-pre-wrap break-words text-sm text-slate-800 dark:text-slate-200">{r.feedback.text}</p>
                        <p className="mt-1 text-xs text-slate-400">
                          {r.feedback.author_name ? `${r.feedback.author_name} · ` : ''}{new Date(r.feedback.feedback_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      {viewing && (
        <HomeworkFileViewerModal
          title={viewing.title}
          fetchBlob={() => adminApi.downloadStudentHomework(entryId, viewing.submissionId).then((r) => r.data)}
          onClose={() => setViewing(null)}
        />
      )}
    </DashboardLayout>
  );
}
